/**
 * Bar Intelligence™ — period highs and lows, measured against the current bar.
 *
 * Spec and every decision behind it: `docs/plans/bar-intelligence.md`. This
 * module is **all of the arithmetic** and none of the drawing: the server sends
 * facts (bars, and a pattern's anchor prices), and every number a reader sees —
 * distance, %, near, crossed, duration, range position — is computed here, so a
 * settings change redraws without a refetch and every tick moves the numbers.
 *
 * ## Two families
 *
 * - **Short-term** `dH dL · wH wL · mH mL` — the previous session, the previous
 *   calendar week, the previous calendar month. They renew every period, so a
 *   cross only highlights; nothing fades or hides.
 * - **Long-term** `QTD · YTD · 52W · ATH` — records. **Frozen as of the prior
 *   bar**: a YTD high that included the current bar could never be crossed,
 *   because the day price goes above it the current bar *is* the YTD high. So
 *   each level is the extreme of the bars *before* the current one, and a level
 *   crossed by `cross` % stays, faded, for `hideDays` trading days.
 *
 * ## Daily bars, whatever the chart
 *
 * Every level is a fact about daily bars. On a daily chart those are the chart's
 * own bars; on any other interval the server sends them (`dailyContext`, a
 * `qf.geometry` shape) with a seed for the all-time extremes older than the
 * window. Either way they arrive here as one array, and the **current bar is the
 * chart's own last bar** (owner, 2026-10-12): on a 5-minute chart, the last
 * five-minute bar's High or Close.
 *
 * Days are trading days: a duration is a count of daily bars, never a calendar
 * difference.
 */

/** One daily bar. `t` is epoch seconds, UTC midnight for a daily bar. */
export interface BiBar {
  t: number;
  h: number;
  l: number;
  c: number;
}

/** An all-time extreme from before the bars sent, and how many bars before them it was set. */
export interface BiSeed {
  p: number;
  t: number;
  /** Trading days from the seed's bar to the first bar sent (1 = the bar just before). */
  before: number;
}

export interface BiSeeds {
  ath?: BiSeed;
  atl?: BiSeed;
}

/**
 * The bar distances are measured from, and the extremes the cross test reads.
 *
 * On a daily chart both are the last bar. On an intraday chart the cross test
 * reads every bar of today — a level crossed at 10:00 stays crossed at 15:00
 * (Q16) — while the distance reads the last bar alone.
 */
export interface BiCurrent {
  t: number;
  h: number;
  l: number;
  c: number;
  /** Today's highest High / Close, and lowest Low / Close, for the cross and "new today". */
  today?: { h: number; l: number; cMax: number; cMin: number };
}

export type BiMeasure = "high" | "close";

/**
 * The indicator's settings — `barIntel_<near>_<cross>_<hide>_<measure>_<entry>`, or
 * `paLevels_<near>_<cross>_<hide>_<measure>_<reference>_<record>_<entry>`.
 */
export interface BiParams {
  /** Within this % of a level, the level is highlighted. */
  near: number;
  /** Beyond a level by this %, it is crossed. */
  cross: number;
  /** Trading days a crossed long-term level stays before it disappears. */
  hideDays: number;
  /** High (Low against a low) or Close — Q9: the setting flips on the low side. */
  measure: BiMeasure;
  /** Draw the reference levels (`dH … qL`). Only PA Levels can; the table always has them. */
  shortTerm: boolean;
  /** Draw the record levels (MTD … ATH). */
  longTerm: boolean;
  /** The inside setup's buy stop: this many dollars above the inside bar's high (owner, 2026-10-13). */
  entry: number;
}

export const BI_DEFAULTS: BiParams = {
  near: 2,
  cross: 2,
  hideDays: 5,
  measure: "high",
  shortTerm: true,
  longTerm: true,
  entry: 0.1,
};

/** The aliases' first segments; one place, so the catalog and the client cannot drift. */
export const BAR_INTEL_ALIAS = "barIntel";
/** The owner's superset (category PD, `qf-pd`): the reference levels drawn as well. */
export const PA_LEVELS_ALIAS = "paLevels";

/** The ceiling the server's window is sized for: 252 bars of 52W plus this. */
export const MAX_HIDE_DAYS = 60;

/**
 * The settings off an alias, defaults for anything missing or unreadable.
 *
 * **Read positionally from the front, and every new parameter is appended** —
 * Volume by Price's rule: a stored alias is a plot somebody has.
 */
export function barIntelParams(alias: string | undefined): BiParams {
  const parts = (alias ?? "").split("_");
  if (parts[0] !== BAR_INTEL_ALIAS && parts[0] !== PA_LEVELS_ALIAS) return { ...BI_DEFAULTS };
  const pa = parts[0] === PA_LEVELS_ALIAS;
  const num = (s: string | undefined, d: number, lo: number, hi: number) => {
    const v = s === undefined ? NaN : Number(s);
    return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d;
  };
  const onOff = (s: string | undefined, d: boolean) => (s === "on" ? true : s === "off" ? false : d);
  return {
    near: num(parts[1], BI_DEFAULTS.near, 0, 100),
    cross: num(parts[2], BI_DEFAULTS.cross, 0, 100),
    hideDays: Math.round(num(parts[3], BI_DEFAULTS.hideDays, 0, MAX_HIDE_DAYS)),
    measure: parts[4] === "close" ? "close" : parts[4] === "high" ? "high" : BI_DEFAULTS.measure,
    // Bar Intelligence draws the record levels only (owner, 2026-10-12); an
    // alias minted before that still carries `_on_on`, which is ignored.
    shortTerm: pa ? onOff(parts[5], BI_DEFAULTS.shortTerm) : false,
    longTerm: pa ? onOff(parts[6], BI_DEFAULTS.longTerm) : true,
    // Appended last on each (owner, 2026-10-13); an older alias has none and
    // reads the default.
    entry: num(pa ? parts[7] : parts[5], BI_DEFAULTS.entry, 0, 1000),
  };
}

export function isBarIntelAlias(alias: string | undefined): boolean {
  const head = (alias ?? "").split("_", 1)[0];
  return head === BAR_INTEL_ALIAS || head === PA_LEVELS_ALIAS;
}

export function isPaLevelsAlias(alias: string | undefined): boolean {
  return (alias ?? "").split("_", 1)[0] === PA_LEVELS_ALIAS;
}

// ---------------------------------------------------------------------------
// Calendar keys — UTC, which is the date a daily bar is stamped with.
// ---------------------------------------------------------------------------

const DAY = 86_400;

export function dayKey(t: number): number {
  return Math.floor(t / DAY);
}
/** Monday-based week: 1970-01-01 was a Thursday, so Monday 1969-12-29 is day −3. */
export function weekKey(t: number): number {
  return Math.floor((dayKey(t) + 3) / 7);
}
export function monthKey(t: number): number {
  const d = new Date(t * 1000);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
}
export function quarterKey(t: number): number {
  const d = new Date(t * 1000);
  return d.getUTCFullYear() * 4 + Math.floor(d.getUTCMonth() / 3);
}
export function yearKey(t: number): number {
  return new Date(t * 1000).getUTCFullYear();
}

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------

/** `pq` is the last completed quarter (`qH`); `q` is quarter to date (QTD). */
export type BiPeriod = "d" | "w" | "m" | "pq" | "mtd" | "q" | "y" | "52w" | "ath";
export type BiSide = "high" | "low";
export type BiFamily = "short" | "long";

export const SHORT_PERIODS: readonly BiPeriod[] = ["d", "w", "m", "pq"];
export const LONG_PERIODS: readonly BiPeriod[] = ["mtd", "q", "y", "52w", "ath"];

/** Longest first — the order a stack picks its top label in (Q18). */
const RANK: Record<BiPeriod, number> = { ath: 9, "52w": 8, y: 7, q: 6, mtd: 5, pq: 4, m: 3, w: 2, d: 1 };

/** The record levels that count touches (owner, 2026-10-12). */
const TOUCH_PERIODS: ReadonlySet<BiPeriod> = new Set(["y", "52w"]);

export function familyOf(period: BiPeriod): BiFamily {
  return SHORT_PERIODS.includes(period) ? "short" : "long";
}

/** `dH`, `wL`, `YTD H`, `ATH` … — the spelling on the chart and in the panel. */
export function levelName(period: BiPeriod, side: BiSide): string {
  const s = side === "high" ? "H" : "L";
  switch (period) {
    case "d":
      return `d${s}`;
    case "w":
      return `w${s}`;
    case "m":
      return `m${s}`;
    case "pq":
      return `q${s}`;
    case "mtd":
      return `MTD ${s}`;
    case "q":
      return `QTD ${s}`;
    case "y":
      return `YTD ${s}`;
    case "52w":
      return `52W ${s}`;
    case "ath":
      return side === "high" ? "ATH" : "ATL";
  }
}

/** The panel's row heading. */
export function periodLabel(period: BiPeriod): string {
  return {
    d: "Yesterday",
    w: "Last week",
    m: "Last month",
    pq: "Last quarter",
    mtd: "MTD",
    q: "QTD",
    y: "YTD",
    "52w": "52W",
    ath: "All-time",
  }[period];
}

/** 52W is the last 252 trading bars (Q2). */
export const WEEKS_52_BARS = 252;

/** Index range `[from, to)` of `bars` a period covers, as of a bar at `t` with `n` bars before it. */
function window(period: BiPeriod, bars: readonly BiBar[], n: number, t: number): [number, number] {
  switch (period) {
    case "d":
      return [Math.max(0, n - 1), n];
    case "w":
    case "m":
    case "pq": {
      // The most recent *completed* week (month, quarter) before this bar's —
      // holidays and a missing week leave no hole, because it is the latest one present.
      const key = period === "w" ? weekKey : period === "m" ? monthKey : quarterKey;
      const now = key(t);
      let to = n;
      while (to > 0 && key(bars[to - 1]!.t) >= now) to--;
      if (to === 0) return [0, 0];
      const prev = key(bars[to - 1]!.t);
      let from = to;
      while (from > 0 && key(bars[from - 1]!.t) === prev) from--;
      return [from, to];
    }
    case "mtd":
    case "q":
    case "y": {
      const key = period === "mtd" ? monthKey : period === "q" ? quarterKey : yearKey;
      const now = key(t);
      let from = n;
      while (from > 0 && key(bars[from - 1]!.t) === now) from--;
      return [from, n];
    }
    case "52w":
      return [Math.max(0, n - WEEKS_52_BARS), n];
    case "ath":
      return [0, n];
  }
}

/** A level as it stood: the price, and the index of the bar that set it (negative: a seed). */
interface Extreme {
  price: number;
  setIdx: number;
  setT: number;
}

function extremeOf(
  period: BiPeriod,
  side: BiSide,
  bars: readonly BiBar[],
  n: number,
  t: number,
  seeds: BiSeeds | undefined,
): Extreme | undefined {
  const [from, to] = window(period, bars, n, t);
  let best: Extreme | undefined;
  for (let i = from; i < to; i++) {
    const p = side === "high" ? bars[i]!.h : bars[i]!.l;
    if (!Number.isFinite(p)) continue;
    // `>=`/`<=`: on a tie the *later* bar set it — the level was last tested there.
    if (!best || (side === "high" ? p >= best.price : p <= best.price)) {
      best = { price: p, setIdx: i, setT: bars[i]!.t };
    }
  }
  if (period === "ath") {
    const seed = side === "high" ? seeds?.ath : seeds?.atl;
    if (seed && Number.isFinite(seed.p) && (!best || (side === "high" ? seed.p > best.price : seed.p < best.price))) {
      best = { price: seed.p, setIdx: -seed.before, setT: seed.t };
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Measuring
// ---------------------------------------------------------------------------

/** The price a side is measured from: High → the bar's High (its Low against a low), or Close. */
function measureOf(bar: { h: number; l: number; c: number }, side: BiSide, measure: BiMeasure): number {
  if (measure === "close") return bar.c;
  return side === "high" ? bar.h : bar.l;
}

/** How far past the level, as a fraction, in the direction that crosses it: + is through. */
function penetration(price: number, level: number, side: BiSide): number {
  return side === "high" ? (price - level) / level : (level - price) / level;
}

export type BiState = "far" | "near" | "through" | "crossed";

export interface BiLevel {
  key: string;
  period: BiPeriod;
  side: BiSide;
  family: BiFamily;
  name: string;
  price: number;
  /** When the bar that set it traded, epoch seconds. */
  setT: number;
  /** Trading days from the setting bar to the current bar. */
  days: number;
  /** current − level, signed: − below, + above (D5). */
  distance: number;
  /** The same, as a % of the level. */
  distancePct: number;
  state: BiState;
  /** For a crossed long-term level: trading days since the cross, and how many it stays. */
  crossed?: { t: number; daysAgo: number; left: number };
  /** YTD and 52W: retests since the setting bar — runs of settled bars within Near %. */
  touches?: number;
}

export interface BiResult {
  levels: BiLevel[];
  /** Where the close sits between each long-term period's low and high, 0–100. */
  rangePos: { period: BiPeriod; pct: number }[];
  /** "★ new MTD low today" — every active level today's bar went through. */
  newToday: { period: BiPeriod; side: BiSide; name: string }[];
}

/**
 * Every level, measured against `current` — all of them whatever the family
 * toggles say, because the table is the same for everyone (owner, 2026-10-12);
 * the toggles decide only what is drawn.
 *
 * `daily` is settled daily bars, ascending; any on or after the current bar's
 * own session are dropped here, so a caller may pass the whole file. `daily`
 * on a daily chart is the chart's bars *without* the last one.
 */
export function computeLevels(
  dailyIn: readonly BiBar[],
  current: BiCurrent,
  params: BiParams,
  options: { seeds?: BiSeeds; periods?: readonly BiPeriod[] } = {},
): BiResult {
  const today = dayKey(current.t);
  let n = dailyIn.length;
  while (n > 0 && dayKey(dailyIn[n - 1]!.t) >= today) n--;
  const daily = n === dailyIn.length ? dailyIn : dailyIn.slice(0, n);
  const seeds = options.seeds;
  const allowed = new Set(options.periods ?? [...SHORT_PERIODS, ...LONG_PERIODS]);
  const periods = [...SHORT_PERIODS, ...LONG_PERIODS].filter((p) => allowed.has(p));

  const extremes = { h: current.today?.h ?? current.h, l: current.today?.l ?? current.l };
  const closeExt = { max: current.today?.cMax ?? current.c, min: current.today?.cMin ?? current.c };
  // What today's cross test reads, per side.
  const todayMeasure = (side: BiSide) =>
    params.measure === "close"
      ? side === "high"
        ? closeExt.max
        : closeExt.min
      : side === "high"
        ? extremes.h
        : extremes.l;

  const levels: BiLevel[] = [];
  const active = new Map<string, BiLevel>();
  const newToday: BiResult["newToday"] = [];

  for (const period of periods) {
    const family = familyOf(period);
    for (const side of ["high", "low"] as const) {
      const ext = extremeOf(period, side, daily, n, current.t, seeds);
      if (!ext) continue;
      const level = measureLevel(period, side, ext, n, current, params);
      if (TOUCH_PERIODS.has(period)) level.touches = touchesOf(daily, ext, n, side, params);
      const through = penetration(todayMeasure(side), ext.price, side);
      if (family === "long" && through * 100 >= params.cross && params.cross > 0) {
        level.state = "crossed";
        level.crossed = { t: current.t, daysAgo: 0, left: params.hideDays };
      }
      levels.push(level);
      active.set(`${period}:${side}`, level);
      const extremeToday = side === "high" ? extremes.h : extremes.l;
      if (side === "high" ? extremeToday > ext.price : extremeToday < ext.price) {
        newToday.push({ period, side, name: levelName(period, side) });
      }

      // **The ghosts: long-term levels crossed on a past bar, still inside
      // `hideDays`.** Replayed from the bars themselves rather than remembered,
      // so a reload, a second device and a settings change all agree.
      if (family === "long" && params.cross > 0 && params.hideDays > 0) {
        for (let i = Math.max(0, n - params.hideDays + 1); i < n; i++) {
          const past = extremeOf(period, side, daily, i, daily[i]!.t, seeds);
          if (!past) continue;
          const pen = penetration(measureOf(daily[i]!, side, params.measure), past.price, side);
          if (pen * 100 < params.cross) continue;
          const ghost = measureLevel(period, side, past, n, current, params);
          ghost.key = `${period}:${side}:x${daily[i]!.t}`;
          ghost.state = "crossed";
          const daysAgo = n - i;
          ghost.crossed = { t: daily[i]!.t, daysAgo, left: Math.max(0, params.hideDays - daysAgo) };
          levels.push(ghost);
        }
      }
    }
  }

  const rangePos: BiResult["rangePos"] = [];
  for (const period of periods) {
    if (familyOf(period) !== "long") continue;
    const hi = active.get(`${period}:high`);
    const lo = active.get(`${period}:low`);
    if (!hi || !lo || hi.price <= lo.price) continue;
    rangePos.push({ period, pct: ((current.c - lo.price) / (hi.price - lo.price)) * 100 });
  }

  return { levels, rangePos, newToday };
}

/**
 * Retests of a level since the bar that set it: runs of consecutive settled bars
 * whose measure came within `near` % of it. A run is one touch; the current bar
 * is not counted — its state is the ◆.
 */
function touchesOf(daily: readonly BiBar[], ext: Extreme, n: number, side: BiSide, params: BiParams): number {
  let touches = 0;
  let inside = false;
  for (let i = Math.max(0, ext.setIdx + 1); i < n; i++) {
    const short = -penetration(measureOf(daily[i]!, side, params.measure), ext.price, side) * 100;
    const near = short <= params.near;
    if (near && !inside) touches++;
    inside = near;
  }
  return touches;
}

function measureLevel(
  period: BiPeriod,
  side: BiSide,
  ext: Extreme,
  n: number,
  current: BiCurrent,
  params: BiParams,
): BiLevel {
  const m = measureOf(current, side, params.measure);
  const distance = m - ext.price;
  const distancePct = (distance / ext.price) * 100;
  const pen = -penetration(m, ext.price, side) * 100; // + = still short of it
  const state: BiState = pen <= 0 ? "through" : pen <= params.near ? "near" : "far";
  return {
    key: `${period}:${side}`,
    period,
    side,
    family: familyOf(period),
    name: levelName(period, side),
    price: ext.price,
    setT: ext.setT,
    days: n - ext.setIdx,
    distance,
    distancePct,
    state,
  };
}

// ---------------------------------------------------------------------------
// Stacking — "draw only the top one, hover shows all" (D8, Q18)
// ---------------------------------------------------------------------------

export interface BiStack {
  /** The longest period's level, which carries the label. */
  top: BiLevel;
  /** Everything in the stack, top first. */
  all: BiLevel[];
}

/**
 * Levels whose labels would overlap, grouped, longest period on top.
 *
 * "Would overlap on screen" is measured as a share of the price span the chart
 * is showing (`span`): two labels closer than `tolerance × span` collide. The
 * renderer does not tell the host its pixel scale, and the span of the visible
 * bars is the number that scale is made of.
 */
export function stackLevels(levels: readonly BiLevel[], span: number, tolerance = 0.025): BiStack[] {
  const sorted = [...levels].sort((a, b) => a.price - b.price);
  const gap = Math.max(0, span) * tolerance;
  const groups: BiLevel[][] = [];
  for (const level of sorted) {
    const last = groups[groups.length - 1];
    if (last && level.price - last[last.length - 1]!.price <= gap) last.push(level);
    else groups.push([level]);
  }
  return groups.map((group) => {
    const all = [...group].sort(
      (a, b) =>
        RANK[b.period] - RANK[a.period] ||
        // A live level above its own ghost.
        Number(a.state === "crossed") - Number(b.state === "crossed"),
    );
    return { top: all[0]!, all };
  });
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

export function fmtPrice(p: number): string {
  if (!Number.isFinite(p)) return "—";
  const abs = Math.abs(p);
  return p.toFixed(abs >= 1000 ? 2 : abs >= 1 ? 2 : 4);
}

export function fmtSigned(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return "—";
  const s = Math.abs(v).toFixed(digits);
  return v > 0 ? `+${s}` : v < 0 ? `−${s}` : s;
}

/** `YTD H 186.40 · 91d · −8.95 · −4.8%`, plus ◆ near / ✕ crossed. */
export function levelText(level: BiLevel): string {
  let text = `${level.name} ${fmtPrice(level.price)} · ${level.days}d · ${fmtSigned(level.distance)} · ${fmtSigned(level.distancePct, 1)}%`;
  if (level.state === "near") text += " ◆";
  if (level.crossed) text += ` · ✕ ${level.crossed.daysAgo}d`;
  return text;
}

/** The label a stack carries: its top level's text, the other names joined in front. */
export function stackText(stack: BiStack): string {
  if (stack.all.length === 1) return levelText(stack.top);
  const others = stack.all.length - 1;
  return `${levelText(stack.top)} (+${others})`;
}

/** The hover popup's lines for a stack — every level in it, set date included. */
export function stackHover(stack: BiStack): string[] {
  return stack.all.map((l) => {
    const set = new Date(l.setT * 1000).toISOString().slice(0, 10);
    return `${l.name}  ${fmtPrice(l.price)}  set ${set} · ${l.days}d · ${fmtSigned(l.distance)} · ${fmtSigned(l.distancePct, 1)}%${l.touches !== undefined ? ` · ${l.touches} touch${l.touches === 1 ? "" : "es"}` : ""}${l.crossed ? ` · crossed ${l.crossed.daysAgo}d ago (${l.crossed.left} left)` : ""}`;
  });
}

/** The short-term axis tag: `dH`, or `dH·wH` when two coincide. */
export function stackTag(stack: BiStack): string {
  return stack.all.map((l) => l.name).join("·");
}
